import React, { JSX, RefObject } from "react";
import { MenuItem, TextField, Typography } from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  FieldPath,
  FieldValues,
  RegisterOptions
} from "react-hook-form";
import { get, startCase } from "lodash";

interface FormTextFieldProps {
  style?: { [key: string]: string | number };
  control: Control<FieldValues>;
  name: string;
  label: string;
  rules?: Omit<
    RegisterOptions<FieldValues, FieldPath<FieldValues>>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  errors: FieldErrors;
  defaultValue?: string | number | boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  inputRef?: RefObject<HTMLInputElement> | null;
  options?: { value: string | number; label: string; description?: string }[] | null;
  select?: boolean;
  multiline?: boolean;
  dataTestmation: string;
  renderValueAsLabel?: boolean;
  rows?: number;
  onValueChange?: (value: string) => void;
  type?: string;
}

const getError = (errors, name) => get(errors, name, "");

const FormTextField = ({
  style = {},
  control,
  name,
  rules = {},
  label,
  errors,
  defaultValue = "",
  fullWidth = false,
  disabled = false,
  className = "",
  placeholder = "",
  inputRef = null,
  select = false,
  multiline = false,
  options = null,
  dataTestmation,
  renderValueAsLabel = false,
  rows = 4,
  onValueChange,
  type = "text"
}: FormTextFieldProps): JSX.Element => (
  <Controller
    name={name}
    control={control}
    rules={rules}
    defaultValue={defaultValue}
    render={({ field: { onChange, ...rest } }) => (
      <TextField
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        type={type}
        onChange={(e) => {
          onChange(e);
          if (onValueChange) onValueChange(e.target.value);
        }}
        data-testmation={dataTestmation}
        sx={{
          ...(style || {}),
          ...(multiline
            ? {
                "& textarea": {
                  resize: "vertical"
                }
              }
            : {})
        }}
        inputRef={inputRef}
        disabled={disabled}
        fullWidth={fullWidth}
        label={`${label}${rules?.required ? "*" : ""}`}
        variant="outlined"
        error={!!getError(errors, name)}
        helperText={getError(errors, name)?.message || " "}
        className={className}
        placeholder={placeholder}
        onFocus={(e) => !select && e.target.select()}
        multiline={multiline}
        rows={multiline ? rows : 1}
        select={select}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(select && renderValueAsLabel
          ? { SelectProps: { renderValue: (item) => <Typography>{startCase(item)}</Typography> } }
          : {})}
      >
        {select &&
          options?.map((item) => (
            <MenuItem
              value={item.value}
              key={item.label}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start"
              }}
            >
              <Typography>{item.label}</Typography>
              <Typography fontSize="10px" color="grey">
                {item.description || ""}
              </Typography>
            </MenuItem>
          ))}
      </TextField>
    )}
  />
);

export default FormTextField;
